export const FRAME_SHAPES = [
  {
    id: 'rectangleSoft',
    label: 'Rectangle doux',
    generator: (samples = 96) => roundedRectPoints(1, 0.82, 0.16, samples),
  },
  {
    id: 'rectangleSharp',
    label: 'Rectangle anguleux',
    generator: () => polygonPoints([
      [-1, -0.8],
      [1, -0.8],
      [1, 0.8],
      [-1, 0.8],
    ]),
  },
  {
    id: 'pantos',
    label: 'Pantos',
    generator: (samples = 96) => superellipsePoints(1, 0.95, 2.5, samples, { topBias: 0.1 }),
  },
  {
    id: 'oval',
    label: 'Ovale',
    generator: (samples = 96) => ellipsePoints(1, 0.78, samples),
  },
  {
    id: 'round',
    label: 'Ronde',
    generator: (samples = 96) => ellipsePoints(0.95, 0.95, samples),
  },
  {
    id: 'aviator',
    label: 'Pilote',
    generator: (samples = 120) => aviatorPoints(samples),
  },
  {
    id: 'catEye',
    label: 'Oeil de chat léger',
    generator: (samples = 110) => catEyePoints(samples),
  },
  {
    id: 'butterfly',
    label: 'Papillon',
    generator: (samples = 110) => butterflyPoints(samples),
  },
  {
    id: 'hexSoft',
    label: 'Hexagone doux',
    generator: () => polygonPoints([
      [-0.95, -0.2],
      [-0.65, -0.82],
      [0.65, -0.82],
      [0.95, -0.2],
      [0.7, 0.82],
      [-0.7, 0.82],
    ]),
  },
  {
    id: 'oversizeRound',
    label: 'Oversize arrondie',
    generator: (samples = 100) => superellipsePoints(1.06, 0.9, 3.6, samples, { topBias: 0.06 }),
  },
];

export function getFrameShape(shapeId) {
  return FRAME_SHAPES.find((shape) => shape.id === shapeId) ?? FRAME_SHAPES[0];
}

export function getScaledShapePoints(shapeId, A, B, samples = 120) {
  const shape = getFrameShape(shapeId);
  return shape.generator(samples).map(([x, y]) => ({
    x: x * (A / 2),
    y: y * (B / 2),
  }));
}

function ellipsePoints(rx, ry, samples) {
  return Array.from({ length: samples }, (_, i) => {
    const t = (i / samples) * Math.PI * 2;
    return [Math.cos(t) * rx, Math.sin(t) * ry];
  });
}

function superellipsePoints(rx, ry, exponent, samples, options = {}) {
  const { topBias = 0 } = options;
  return Array.from({ length: samples }, (_, i) => {
    const t = (i / samples) * Math.PI * 2;
    const ct = Math.cos(t);
    const st = Math.sin(t);
    const x = Math.sign(ct) * Math.pow(Math.abs(ct), 2 / exponent) * rx;
    const yBase = Math.sign(st) * Math.pow(Math.abs(st), 2 / exponent) * ry;
    const y = st > 0 ? yBase * (1 - topBias) : yBase * (1 + topBias * 0.3);
    return [x, y];
  });
}

function roundedRectPoints(rx, ry, radiusRatio, samples) {
  const r = Math.min(rx, ry) * radiusRatio;
  const innerX = rx - r;
  const innerY = ry - r;
  const quarter = Math.max(8, Math.floor(samples / 4));
  const centers = [
    [innerX, innerY, 0],
    [-innerX, innerY, Math.PI / 2],
    [-innerX, -innerY, Math.PI],
    [innerX, -innerY, (3 * Math.PI) / 2],
  ];

  return centers.flatMap(([cx, cy, start], idx) => {
    const limit = idx === centers.length - 1 ? quarter + 1 : quarter;
    return Array.from({ length: limit }, (_, i) => {
      const t = start + (i / quarter) * (Math.PI / 2);
      return [cx + Math.cos(t) * r, cy + Math.sin(t) * r];
    });
  });
}

function polygonPoints(points) {
  return points;
}

function aviatorPoints(samples) {
  return Array.from({ length: samples }, (_, i) => {
    const t = (i / samples) * Math.PI * 2;
    const ct = Math.cos(t);
    const st = Math.sin(t);
    const x = 0.9 * ct + 0.12 * Math.sin(2 * t);
    const topFactor = st > 0 ? 0.78 : 1.02;
    const y = st * topFactor - 0.1;
    return [x, y];
  });
}

function catEyePoints(samples) {
  return Array.from({ length: samples }, (_, i) => {
    const t = (i / samples) * Math.PI * 2;
    const ct = Math.cos(t);
    const st = Math.sin(t);
    const lift = Math.max(0, ct) * Math.max(0, st) * 0.28;
    const x = ct * (0.95 + Math.max(0, st) * 0.08);
    const y = st * 0.78 + lift;
    return [x, y];
  });
}

function butterflyPoints(samples) {
  return Array.from({ length: samples }, (_, i) => {
    const t = (i / samples) * Math.PI * 2;
    const ct = Math.cos(t);
    const st = Math.sin(t);
    const wing = 0.2 * Math.sin(2 * t);
    const x = ct * (1 + 0.08 * Math.abs(st));
    const y = st * (0.78 + 0.22 * Math.abs(ct)) + wing * 0.12;
    return [x, y];
  });
}
